import { AlertTriangle, ExternalLink } from "lucide-react";

import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { safeHostname } from "@/marketing/lib/formatters";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";

import { CampaignLimitationsCard } from "./CampaignLimitationsCard";

interface BriefAssetsTabProps {
  campaign: InfluencerCampaign;
}

export function BriefAssetsTab({ campaign }: BriefAssetsTabProps) {
  const lookupBrand = useBrandLookup();
  const brandName =
    lookupBrand(campaign.brandId)?.name ?? campaign.brandId.slice(0, 8);
  const briefLines = (campaign.brief ?? "").split("\n");
  const refs = campaign.productRefs ?? [];

  return (
    <div className="tw-grid tw-gap-4 tw-px-8 tw-py-6 lg:tw-grid-cols-[2fr_1fr]">
      {/* Brief doc */}
      <article className="tw-min-h-[480px] tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-6 tw-text-[14px] tw-leading-relaxed">
        <h2 className="tw-mb-3 tw-text-lg tw-font-semibold tw-tracking-tight">
          {campaign.name}
        </h2>
        <h3 className="tw-mb-1 tw-mt-4 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Brief
        </h3>
        {briefLines.length === 0 ||
        (briefLines.length === 1 && (briefLines[0]?.trim() ?? "") === "") ? (
          <p className="tw-text-muted-foreground">
            No brief written yet. Update the campaign to add one.
          </p>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-text-foreground/90">
            {briefLines.map((line, i) =>
              line.trim().length === 0 ? (
                <span key={i} className="tw-block tw-h-2" />
              ) : (
                <p key={i}>{line}</p>
              ),
            )}
          </div>
        )}

        <h3 className="tw-mb-1 tw-mt-6 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Brand
        </h3>
        <p className="tw-text-foreground/90">{brandName}</p>

        <h3 className="tw-mb-1 tw-mt-6 tw-text-[13px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Targets
        </h3>
        <ul className="tw-ml-4 tw-list-disc tw-text-foreground/90">
          <li>Onboarding target: {campaign.onboardingTarget ?? "—"}</li>
          <li>Posts target: {campaign.postTarget ?? "—"}</li>
        </ul>
      </article>

      {/* Side rail */}
      <aside className="tw-flex tw-flex-col tw-gap-3">
        {campaign.consideration && (
          <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4 tw-text-amber-900">
            <div className="tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider">
              <AlertTriangle className="tw-h-3 tw-w-3" />
              Compliance considerations
            </div>
            <p className="tw-text-[12px] tw-leading-relaxed">
              {campaign.consideration}
            </p>
          </div>
        )}

        <CampaignLimitationsCard campaign={campaign} />

        <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-4">
          <div className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Linked products
          </div>
          {refs.length === 0 ? (
            <p className="tw-text-[12px] tw-text-muted-foreground">
              No product references linked yet.
            </p>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              {refs.map((url) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1.5 tw-text-[12px] hover:tw-bg-accent"
                >
                  <ExternalLink className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
                  <span className="tw-flex-1 tw-truncate">
                    {url.replace(/^https?:\/\//, "")}
                  </span>
                  <span className="tw-text-[10px] tw-text-muted-foreground">
                    {safeHostname(url)}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="tw-rounded-lg tw-border tw-border-border tw-bg-card tw-p-4">
          <div className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
            Schedule
          </div>
          <dl className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-1 tw-text-[12px]">
            <dt className="tw-text-muted-foreground">Window</dt>
            <dd>
              {campaign.startAt ? campaign.startAt.slice(0, 10) : "—"} →{" "}
              {campaign.endAt ? campaign.endAt.slice(0, 10) : "—"}
            </dd>
            <dt className="tw-text-muted-foreground">Posting due</dt>
            <dd>
              {campaign.postingDueDate
                ? campaign.postingDueDate.slice(0, 10)
                : "—"}
            </dd>
          </dl>
        </div>
      </aside>
    </div>
  );
}
